import { useId, useState, type ReactNode } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import type { EndReportMeasure, EndReportPriority, EndReportRecord, EndReportStatus } from "../../../../shared/end-report";
import { byPriority } from "./api";
import { RequestEditor, type RequestEditorProps } from "./RequestEditor";
import { ClampedText } from "../components/ClampedText";
import "../work-settings/settings.css";
import "./end-report.css";

export function StatusBadge({ status }: { status: EndReportStatus }) {
  const { t } = useLocale();
  return <span className={`end-report-status is-${status}`}>{t(`endReport.status.${status}`)}</span>;
}

const FILLED: Record<EndReportPriority, number> = { high: 3, medium: 2, low: 1 };

/** Signal bars, no words on screen: the label is for screen readers and the tooltip. */
export function PriorityBars({ priority = "medium" }: { priority?: EndReportPriority }) {
  const { t } = useLocale();
  const label = t(`endReport.priority.${priority}`);
  return <span className={`end-report-priority is-${priority}`} role="img" aria-label={label} title={label}>
    {[0, 1, 2].map((bar) => <i key={bar} className={bar < FILLED[priority] ? "is-on" : undefined} />)}
  </span>;
}

/** lucide `Activity`, `CircleCheck`, `Flag` and `CircleArrowRight`, one per section. */
const SECTION_ICONS = {
  current: <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />,
  results: <><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></>,
  issues: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><path d="M4 22v-7" /></>,
  next: <><circle cx="12" cy="12" r="10" /><path d="M8 12h8" /><path d="m12 16 4-4-4-4" /></>,
};

function SectionIcon({ icon }: { icon: keyof typeof SECTION_ICONS }) {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{SECTION_ICONS[icon]}</svg>;
}

function SectionTitle({ icon, children }: { icon: keyof typeof SECTION_ICONS; children: string }) {
  return <h4 className="end-report-section-title"><SectionIcon icon={icon} />{children}</h4>;
}

/** This round, laid out like the results: its title beside the heading, three items open. */
function CurrentSection({ title, results, resolved }: { title: string; results: string[]; resolved?: { id: string; title: string }[] }) {
  const { t } = useLocale();
  return <section className="end-report-current">
    <h4 className="end-report-section-title">
      <SectionIcon icon="current" />
      <span>{t("endReport.current")}</span>
      <span className="end-report-current-title" title={title}>{title}</span>
    </h4>
    <Capped wrap={(nodes) => <ul className="end-report-results">{nodes}</ul>} items={results.map((result, i) => <li key={i}><ClampedText text={result} /></li>)} />
    {/* Earlier to-dos this round dropped, as the agent confirmed: progress the user can see. */}
    {resolved?.length ? <p className="end-report-resolved">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
      <ClampedText text={t("endReport.resolved", { items: resolved.map((item) => item.title).join(t("endReport.listJoin")) })} />
    </p> : null}
  </section>;
}

/** Built against expected, e.g. 484 / 484 顆, with a bar that fills to the expected count. */
function Measure({ done, expected, unit }: EndReportMeasure) {
  const { t } = useLocale();
  const known = expected !== undefined && expected > 0;
  const ratio = known ? Math.min(1, done / expected) : 0;
  const label = known
    ? t("endReport.measureOf", { done: done.toLocaleString(), expected: expected.toLocaleString(), unit })
    : `${done.toLocaleString()} ${unit}`;
  return <span className={`end-report-measure${known && done >= expected ? " is-full" : ""}`}>
    {known ? <span className="end-report-measure-bar" aria-hidden="true"><i style={{ width: `${ratio * 100}%` }} /></span> : null}
    <span className="end-report-measure-value">{label}</span>
  </span>;
}

const SHOWN = 3;

/**
 * The first three, the rest folded behind "N more": the report should not
 * bury the user. `wrap` builds the list element, used once for each part.
 */
function Capped({ items, wrap = (nodes) => <>{nodes}</> }: { items: ReactNode[]; wrap?: (nodes: ReactNode[]) => ReactNode }) {
  const { t } = useLocale();
  const rest = items.slice(SHOWN);
  return <>
    {wrap(items.slice(0, SHOWN))}
    {rest.length ? <details className="end-report-more">
      <summary>{t("endReport.more", { count: rest.length })}</summary>
      {wrap(rest)}
    </details> : null}
  </>;
}

function Chevron({ back = false }: { back?: boolean }) {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={back ? "m15 5-7 7 7 7" : "m9 5 7 7-7 7"} />
  </svg>;
}

/** A next step's label, tagged when the agent recommends it. */
function StepLabel({ step }: { step: { label: string; recommended?: boolean } }) {
  const { t } = useLocale();
  return <span className="end-report-step-label">
    {step.label}
    {step.recommended ? <span className="end-report-recommended">{t("endReport.recommended")}</span> : null}
  </span>;
}

/**
 * One round of the end report in the side panel: results, open issues and the
 * next steps. Only the latest unanswered round takes a choice; older rounds
 * show what was chosen.
 */
export function EndReportPanel({ record, position, sent, latest, disabled, onPage, onSubmit, note, onNoteChange, requestFocus }: RequestEditorProps & {
  record: EndReportRecord;
  position: { index: number; total: number };
  /** Sent from this page, before the reload shows the saved answer. */
  sent: boolean;
  latest: boolean;
  disabled: boolean;
  onPage: (index: number) => void;
  onSubmit: (selected: string[], note: string) => void;
}) {
  const { t } = useLocale();
  const formId = useId();
  const { submission } = record;
  const { current, work: report } = record.report;
  // Nothing starts ticked: importance is advice, not the user's choice.
  const [selected, setSelected] = useState<string[]>([]);
  const issues = byPriority(report.issues);
  const nextSteps = byPriority(report.nextSteps);
  // The latest round takes an answer even without listed steps: the note alone is one.
  const answerable = latest && !submission && !sent;
  const chosen = submission ? report.nextSteps.filter((step) => submission.selected.includes(step.id)) : [];

  return (
    // No title row and no close: it is a tab of the workflow's view, which names it.
    <aside className="work-settings-panel end-report-panel" aria-label={t("endReport.title")}>
      <nav className="end-report-pager" aria-label={t("endReport.rounds")}>
        <button type="button" className="icon-btn" aria-label={t("endReport.previous")} disabled={position.index <= 0} onClick={() => onPage(position.index - 1)}><Chevron back /></button>
        <span>{t("endReport.roundOf", { round: record.round, total: position.total })}</span>
        <button type="button" className="icon-btn" aria-label={t("endReport.next")} disabled={position.index >= position.total - 1} onClick={() => onPage(position.index + 1)}><Chevron /></button>
      </nav>
      <div className="work-settings-content">
        <div className="work-settings-body end-report-body">
          <div className="end-report-heading">
            <h3>{report.title}</h3>
            <StatusBadge status={report.status} />
          </div>
          <p className="end-report-summary">{report.summary}</p>
          {/* Only what the summary would not say: a decision or a blocker is already in it. */}
          {(report.stopReason === "limit" || report.stopReason === "error") && report.status !== "done" ? (
            <p className="end-report-stop">{t(`endReport.stop.${report.stopReason}`)}</p>
          ) : null}
          <CurrentSection title={current.title} results={current.results} resolved={record.resolved} />

          {report.results.length ? <section>
            <SectionTitle icon="results">{t("endReport.results")}</SectionTitle>
            <Capped wrap={(nodes) => <ul className="end-report-results">{nodes}</ul>} items={report.results.map((result, i) => <li key={i}>
              <ClampedText
                text={result.text}
                suffix={!result.checked ? <span className="end-report-unchecked">{t("endReport.unchecked")}</span> : null}
              />
              {result.measure ? <Measure {...result.measure} /> : null}
            </li>)} />
          </section> : null}

          {report.issues.length ? <section>
            <SectionTitle icon="issues">{t("endReport.issues")}</SectionTitle>
            <Capped items={issues.map((issue) => <details key={issue.id} className="end-report-issue">
              <summary>
                <PriorityBars priority={issue.priority} />
                <span className="end-report-issue-title">{issue.title}</span>
                <span className="end-report-count">{t("endReport.count", { count: issue.count })}</span>
              </summary>
              <ul>
                {issue.examples.map((example, i) => <li key={i}>
                  <span>{example.text}</span>
                  {example.elementIds?.length ? <span className="end-report-ids" title={t("endReport.elementIds")}>ID {example.elementIds.join(", ")}</span> : null}
                </li>)}
              </ul>
              {issue.count > issue.examples.length ? (
                <p className="end-report-rest">{t("endReport.restInList", { count: issue.count - issue.examples.length })}</p>
              ) : null}
            </details>)} />
          </section> : null}

          {answerable ? (
            <form id={formId} className="end-report-next" onSubmit={(event) => {
              event.preventDefault();
              if (!disabled && (selected.length || note.trim())) onSubmit(selected, note);
            }}>
              {nextSteps.length ? <SectionTitle icon="next">{t("endReport.nextSteps")}</SectionTitle> : null}
              {/* Every choice in view: folding checkboxes hides what can be picked. */}
              {nextSteps.map((step) => <label key={step.id} className="end-report-option">
                <input
                  type="checkbox"
                  checked={selected.includes(step.id)}
                  disabled={disabled}
                  onChange={(event) => setSelected((list) => event.target.checked ? [...list, step.id] : list.filter((id) => id !== step.id))}
                />
                <PriorityBars priority={step.priority} />
                <StepLabel step={step} />
              </label>)}
              <RequestEditor note={note} onNoteChange={onNoteChange} requestFocus={requestFocus} disabled={disabled} />
            </form>
          ) : submission || sent ? (
            <section className="end-report-answer">
              <SectionTitle icon="next">{t("endReport.chosen")}</SectionTitle>
              {chosen.length ? <ul>{chosen.map((step) => <li key={step.id}><StepLabel step={step} /></li>)}</ul> : null}
              {submission?.note ? <p>{submission.note}</p> : null}
              <span className="end-report-sent">{t("workSettings.submitted")}</span>
            </section>
          ) : report.nextSteps.length && !latest ? (
            <section>
              <SectionTitle icon="next">{t("endReport.nextSteps")}</SectionTitle>
              <ul>{nextSteps.map((step) => <li key={step.id}><StepLabel step={step} /></li>)}</ul>
            </section>
          ) : null}
          {latest && !answerable ? (
            <form id={formId} className="end-report-next" onSubmit={(event) => {
              event.preventDefault();
              if (!disabled && note.trim()) onSubmit([], note);
            }}>
              <RequestEditor note={note} onNoteChange={onNoteChange} requestFocus={requestFocus} disabled={disabled} />
            </form>
          ) : null}
        </div>
      </div>
      {latest ? (
        <footer className="work-settings-footer">
          <button type="submit" form={formId} className="btn primary" disabled={disabled || (!(answerable && selected.length) && !note.trim())}>
            {t("endReport.continue")}
          </button>
        </footer>
      ) : null}
    </aside>
  );
}
